import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { publicFormsAPI } from '../../utils/crmApi';
import { useDialog } from '../kibo-ui/dialog';
import { useBranding } from '../../contexts/BrandingContext';

// File upload component (needs hooks)
const FileUploadField = ({ el, value, onChange, formId, helpText, primaryColor = '#3B82F6' }) => {
  const fileValue = value || null;
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be less than 10MB');
      return;
    }
    
    setUploading(true);
    setUploadError('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fieldKey', el.key);
      
      if (!formId) {
        throw new Error('Form ID not found');
      }
      
      const { data } = await publicFormsAPI.uploadFile(formId, formData);
      if (data.success) {
        onChange({
          url: data.data.url,
          name: data.data.name,
          size: data.data.size,
          mime: data.data.mime,
          uploadedAt: new Date().toISOString()
        });
      }
    } catch (e) {
      setUploadError(e.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <div>
      <input
        type="file"
        onChange={handleFileChange}
        disabled={uploading}
        className="w-full px-3 py-2 border rounded file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold"
        style={{
          ['--file-bg-color']: `${primaryColor}15`,
          ['--file-text-color']: primaryColor
        }}
        onMouseEnter={(e) => {
          const fileInput = e.target;
          if (fileInput.files) {
            fileInput.style.setProperty('--file-hover-bg', `${primaryColor}25`);
          }
        }}
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar"
      />
      {uploading && <div className="text-xs text-muted-foreground mt-1">Uploading...</div>}
      {uploadError && <div className="text-xs text-red-600 mt-1">{uploadError}</div>}
      {fileValue && (
        <div className="mt-2 p-2 bg-green-50 dark:bg-green-950/10 border border-green-200 dark:border-green-800 rounded text-xs">
          ✓ {fileValue.name || 'File uploaded'} ({(fileValue.size / 1024).toFixed(1)} KB)
        </div>
      )}
      {helpText && <div className="text-xs text-muted-foreground mt-1">{helpText}</div>}
    </div>
  );
};

const Field = ({ el, value, onChange, locale = 'en', formId, primaryColor = '#3B82F6' }) => {
  const common = {
    className: 'w-full px-3 py-2 border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring',
    value: value ?? '',
    onChange: e => onChange(e.target.value)
  };
  const label = typeof el.label === 'object' ? (el.label[locale] || el.label.en || el.key) : (el.label || el.key);
  const placeholder = typeof el.placeholder === 'object' ? (el.placeholder?.[locale] || el.placeholder?.en || '') : (el.placeholder || '');
  const helpText = typeof el.helpText === 'object' ? (el.helpText?.[locale] || el.helpText?.en) : el.helpText;
  
  if (el.type === 'shortText' || el.type === 'short_text') return <input type="text" {...common} placeholder={placeholder} />;
  if (el.type === 'longText' || el.type === 'long_text') return <textarea rows={4} {...common} placeholder={placeholder} />;
  if (el.type === 'email') return <input type="email" {...common} placeholder={placeholder} />;
  if (el.type === 'phone') return <input type="tel" {...common} placeholder={placeholder} />;
  if (el.type === 'number') return <input type="number" step="1" {...common} placeholder={placeholder} />;
  if (el.type === 'currency') {
    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
        <input type="number" step="0.01" {...common} className="pl-8" placeholder={placeholder} />
      </div>
    );
  }
  if (el.type === 'date') return <input type="date" {...common} />;
  if (el.type === 'description') return (
    <div className="text-sm text-foreground">
      {label && <div className="font-medium">{label}</div>}
      {helpText && <div className="text-muted-foreground text-xs mt-1">{helpText}</div>}
    </div>
  );
  if (el.type === 'title') return (
    <div className="mb-2">
      <h2 className="text-xl font-semibold">{label}</h2>
      {helpText && <div className="text-muted-foreground text-sm mt-1">{helpText}</div>}
    </div>
  );
  if (el.type === 'single_choice') {
    const options = el.options || [];
    return (
      <div>
        {options.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-2 border border-dashed rounded">
            No options configured.
          </div>
        ) : (
          <div className="space-y-2">
            {options.map((opt, idx) => {
              const optLabel = typeof opt.label === 'object' ? (opt.label[locale] || opt.label.en || opt.value) : (opt.label || opt.value);
              const optValue = opt.value || optLabel;
              return (
                <label key={idx} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={el.key}
                    value={optValue}
                    checked={value === optValue}
                    onChange={() => onChange(optValue)}
                    className="w-4 h-4 border-input"
                    style={{ 
                      accentColor: primaryColor,
                      '--tw-ring-color': primaryColor
                    }}
                  />
                  <span className="text-sm text-foreground">{optLabel}</span>
                </label>
              );
            })}
          </div>
        )}
        {helpText && <div className="text-xs text-muted-foreground mt-1">{helpText}</div>}
      </div>
    );
  }
  if (el.type === 'multi_select') {
    const options = el.options || [];
    const selectedValues = Array.isArray(value) ? value : [];
    return (
      <div>
        {options.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-2 border border-dashed rounded">
            No options configured.
          </div>
        ) : (
          <div className="space-y-2">
            {options.map((opt, idx) => {
              const optLabel = typeof opt.label === 'object' ? (opt.label[locale] || opt.label.en || opt.value) : (opt.label || opt.value);
              const optValue = opt.value || optLabel;
              return (
                <label key={idx} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(optValue)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onChange([...selectedValues, optValue]);
                      } else {
                        onChange(selectedValues.filter(v => v !== optValue));
                      }
                    }}
                    className="w-4 h-4 border-input rounded"
                    style={{ 
                      accentColor: primaryColor,
                      '--tw-ring-color': primaryColor
                    }}
                  />
                  <span className="text-sm text-foreground">{optLabel}</span>
                </label>
              );
            })}
          </div>
        )}
        {helpText && <div className="text-xs text-muted-foreground mt-1">{helpText}</div>}
      </div>
    );
  }
  if (el.type === 'page_break') {
    // Page break is structural - in public render, it may trigger auto-advance or be hidden
    return null; // Don't render page breaks in the public form
  }
  if (el.type === 'file_upload') {
    // File upload handled as separate component due to hooks requirement
    return <FileUploadField el={el} value={value} onChange={onChange} formId={formId} helpText={helpText} primaryColor={primaryColor} />;
  }
  return <div className="text-xs text-muted-foreground">Unsupported field: {el.type}</div>;
};

const FormRender = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [pageIdx, setPageIdx] = useState(0);
  const [resumeToken, setResumeToken] = useState(searchParams.get('token') || null);
  const [submitting, setSubmitting] = useState(false);
  const [passwordPrompt, setPasswordPrompt] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [autosaveTimer, setAutosaveTimer] = useState(null);
  const [sessionId] = useState(() => {
    // Generate or retrieve session ID
    const stored = sessionStorage.getItem(`form_session_${slug}`);
    if (stored) return stored;
    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(`form_session_${slug}`, newId);
    return newId;
  });
  const [hasTrackedStart, setHasTrackedStart] = useState(false);
  const { openDialog } = useDialog();

  const loadForm = async (password = '') => {
    try {
      setLoading(true);
      setPasswordError('');
      const params = password ? { password } : {};
      // Include sessionId in params for tracking
      const trackingParams = { ...params, sessionId };
      const { data } = await publicFormsAPI.getBySlug(slug, trackingParams);
      
      // If requiresPassword is true, we need a password
      if (data.requiresPassword) {
        if (!password) {
          // First load - show password prompt
          setPasswordPrompt(true);
          setForm(data.data); // Store form metadata for display
        } else {
          // Password was provided but still requires password = wrong password
          setPasswordPrompt(true);
          setPasswordError('Incorrect password. Please try again.');
          setPasswordInput('');
          setForm(data.data); // Keep form metadata
        }
      } else {
        // Password is correct or not required - show form
        setForm(data.data);
        setAuthenticated(true);
        setPasswordPrompt(false);
        
        // Track view (already tracked on backend, but ensure sessionId is passed)
        // The backend tracks views when fetching the form
        
        // If resumeToken exists in URL, try to load saved progress
        if (resumeToken && data.data.access?.allowSaveResume) {
          loadResumedProgress(data.data._id, resumeToken);
        }
      }
    } catch (e) {
      console.error('Failed to load form:', e);
      if (e.response?.status === 403 || e.response?.status === 401) {
        setPasswordPrompt(true);
        setPasswordError('Password required or incorrect');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForm();
  }, [slug]);
  
  // Load resumed progress
  const loadResumedProgress = async (formId, token) => {
    try {
      const { data } = await publicFormsAPI.resume(formId, token);
      if (data.success && data.data.answers) {
        setAnswers(data.data.answers || {});
        setResumeToken(token);
        // Estimate page index based on answered fields
        const answeredKeys = Object.keys(data.data.answers || {});
        if (form?.pages && answeredKeys.length > 0) {
          for (let i = form.pages.length - 1; i >= 0; i--) {
            const pageFields = (form.pages[i].elements || []).filter(e => e.key).map(e => e.key);
            if (pageFields.some(k => answeredKeys.includes(k))) {
              setPageIdx(i);
              break;
            }
          }
        }
      }
    } catch (e) {
      console.error('Failed to load resumed progress:', e);
    }
  };
  
  // Autosave function
  const autosave = useCallback(async () => {
    if (!form || !form.access?.allowSaveResume || !resumeToken) return;
    try {
      await publicFormsAPI.savePartial(form._id, {
        answers,
        meta: { startedAt: new Date().toISOString(), source: 'public' },
        resumeToken
      });
      setSavedAt(new Date());
    } catch (e) {
      // Silent fail for autosave
    }
  }, [form, answers, resumeToken]);
  
  // Autosave on answer change (debounced)
  useEffect(() => {
    if (autosaveTimer) clearTimeout(autosaveTimer);
    if (form?.access?.allowSaveResume && Object.keys(answers).length > 0) {
      const timer = setTimeout(autosave, 3000); // 3 second debounce
      setAutosaveTimer(timer);
    }
    return () => {
      if (autosaveTimer) clearTimeout(autosaveTimer);
    };
  }, [answers, form]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setPasswordError('');
    if (!passwordInput.trim()) {
      setPasswordError('Password is required');
      return;
    }
    loadForm(passwordInput);
  };

  // Evaluate a single condition rule
  const evaluateCondition = (condition) => {
    if (!condition || !condition.fieldKey) return true;
    const { fieldKey, operator, value } = condition;
    const lhs = answers[fieldKey];
    
    switch (operator) {
      case 'equals':
        if (Array.isArray(lhs)) return lhs.includes(value);
        return String(lhs || '') === String(value || '');
      case 'not_equals':
        return String(lhs || '') !== String(value || '');
      case 'contains':
        return String(lhs || '').toLowerCase().includes(String(value || '').toLowerCase());
      case 'not_contains':
        return !String(lhs || '').toLowerCase().includes(String(value || '').toLowerCase());
      case 'greater_than':
        return Number(lhs || 0) > Number(value || 0);
      case 'less_than':
        return Number(lhs || 0) < Number(value || 0);
      case 'is_empty':
        return lhs === null || lhs === undefined || lhs === '' || (Array.isArray(lhs) && lhs.length === 0);
      case 'is_not_empty':
        return lhs !== null && lhs !== undefined && lhs !== '' && !(Array.isArray(lhs) && lhs.length === 0);
      default:
        return !!lhs;
    }
  };

  // Evaluate a conditions group (with operator: 'and' or 'or')
  const evalGroup = (group) => {
    if (!group) return true;
    if (group.operator === 'and') {
      return Array.isArray(group.rules) ? group.rules.every(evaluateCondition) : true;
    } else if (group.operator === 'or') {
      return Array.isArray(group.rules) ? group.rules.some(evaluateCondition) : false;
    }
    // Legacy format support
    if (group.fieldKey) return evaluateCondition(group);
    return true;
  };

  // Check if a field should be visible based on logic rules
  const isFieldVisible = (fieldId) => {
    const rules = (form?.logic || []).filter(r => r.scope === 'field' && r.targetId === fieldId);
    if (rules.length === 0) return true;
    // Last matching rule wins; actions: show/hide
    let visible = true;
    for (const rule of rules) {
      const pass = evalGroup(rule.conditions);
      if (rule.action === 'show' && pass) visible = true;
      if (rule.action === 'hide' && pass) visible = false;
    }
    return visible;
  };

  const page = useMemo(() => form?.pages?.[pageIdx] || null, [form, pageIdx]);

  const handleNext = async () => {
    if (!form) return;
    const lastPage = (form.pages?.length || 1) - 1;
    if (pageIdx < lastPage) {
      setPageIdx(pageIdx + 1);
      if (form.access?.allowSaveResume) {
        try {
          const { data } = await publicFormsAPI.savePartial(form._id, { answers, meta: { startedAt: new Date().toISOString(), source: 'public' }, resumeToken });
          if (data?.data?.resumeToken) setResumeToken(data.data.resumeToken);
        } catch (_) {}
      }
    }
  };

  const handleSubmit = async () => {
    if (!form) return;
    try {
      setSubmitting(true);
      // Track start on first answer if not already tracked
      if (!hasTrackedStart && Object.keys(answers).length > 0) {
        setHasTrackedStart(true);
      }
      await publicFormsAPI.submit(form._id, { 
        answers, 
        meta: { 
          startedAt: new Date().toISOString(), 
          source: 'public',
          sessionId 
        } 
      });
      
      // Reset form after successful submission
      setAnswers({});
      setPageIdx(0);
      setResumeToken(null);
      setSavedAt(null);
      setHasTrackedStart(false);
      // Clear session storage
      sessionStorage.removeItem(`form_session_${slug}`);
      
      openDialog({
        variant: 'success',
        title: 'Form submitted',
        description: 'Thank you! Your form has been submitted successfully.',
        confirmText: 'OK',
        onConfirm: () => {},
        onClose: () => {}
      });
    } catch (e) {
      openDialog({
        variant: 'error',
        title: 'Submission failed',
        description: e.response?.data?.message || 'An error occurred while submitting the form. Please try again.',
        confirmText: 'OK',
        onConfirm: () => {},
        onClose: () => {}
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!form) return <div className="p-6">Form not found</div>;

  // Show password prompt if required
  if (passwordPrompt && !authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted p-4">
        <div className="bg-card rounded-lg shadow-lg p-8 max-w-md w-full">
          <h2 className="text-2xl font-semibold mb-2">{form.name}</h2>
          <p className="text-muted-foreground mb-6">This form is password protected. Please enter the password to continue.</p>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-2 focus:outline-blue-500"
                placeholder="Enter password"
                autoFocus
              />
              {passwordError && (
                <p className="mt-1 text-sm text-red-600">{passwordError}</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: primaryColor }}
            >
              Continue
            </button>
          </form>
        </div>
      </div>
    );
  }

  const { branding } = useBranding();
  
  // Determine final theme values (agency vs custom)
  const useAgencyBranding = form.theme?.useAgencyBranding !== false; // Default true
  const primaryColor = useAgencyBranding ? (branding?.primaryColor || form.theme?.primary || '#3B82F6') : (form.theme?.primary || '#3B82F6');
  const logoUrl = useAgencyBranding ? (branding?.agencyLogo || form.theme?.logoUrl) : form.theme?.logoUrl;
  const coverImageUrl = useAgencyBranding ? (branding?.agencyCoverImage || form.theme?.coverImageUrl) : form.theme?.coverImageUrl;
  
  return (
    <div className="max-w-2xl mx-auto p-6" dir={form.theme?.rtl ? 'rtl' : 'ltr'} style={{ '--primary-color': primaryColor }}>
      {coverImageUrl && (
        <div className="mb-4 -mx-6 -mt-6 h-32 bg-cover bg-center rounded-t-lg" style={{ backgroundImage: `url(${coverImageUrl})` }} />
      )}
      {logoUrl && (
        <div className="mb-3"><img alt="logo" src={logoUrl} className="h-10" /></div>
      )}
      <h1 className="text-2xl font-semibold mb-1">{form.name}</h1>
      {savedAt && form.access?.allowSaveResume && (
        <div className="mb-3 p-2 bg-green-50 dark:bg-green-950/10 border border-green-200 dark:border-green-800 rounded text-xs text-green-700 dark:text-green-300">
          ✓ Progress saved {savedAt.toLocaleTimeString()}. Your answers are automatically saved.
        </div>
      )}
      {form.settings?.progressBar && (
        <div className="text-sm text-muted-foreground mb-4">Page {pageIdx+1} of {form.pages?.length || 1}</div>
      )}
      {page && (
        <div className="space-y-4">
          {page.elements?.filter(el => isFieldVisible(el.id) && el.type !== 'page_break').map(el => {
            const locale = form.localeDefault || 'en';
            const label = typeof el.label === 'object' ? (el.label[locale] || el.label.en || el.key) : (el.label || el.key);
            return (
              <div key={el.id}>
                {el.type !== 'description' && el.type !== 'title' && (
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {label} {el.required && <span className="text-red-600">*</span>}
                  </label>
                )}
                <Field el={el} value={answers[el.key]} onChange={(v) => setAnswers({ ...answers, [el.key]: v })} locale={locale} formId={form._id} primaryColor={primaryColor} />
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-6 flex items-center gap-2">
        {pageIdx < ((form.pages?.length || 1) - 1) ? (
          <button onClick={handleNext} className="px-4 py-2 rounded text-white hover:opacity-90 transition-opacity" style={{ backgroundColor: primaryColor }}>Next</button>
        ) : (
          <button disabled={submitting} onClick={handleSubmit} className="px-4 py-2 rounded text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: primaryColor }}>Submit</button>
        )}
      </div>
    </div>
  );
};

export default FormRender;


